import { useEffect, useRef } from 'react'

/**
 * Canal BroadcastChannel pour router un click-to-call vers un onglet
 * dashboard déjà ouvert au lieu d'en ouvrir un deuxième.
 *
 * Protocole :
 *   1. Le nouvel onglet `/call?number=...` ouvre le canal et émet `ping`.
 *   2. Un onglet dashboard déjà ouvert répond `pong` (si idle) ou `busy`
 *      (si appel en cours) sous 300 ms.
 *   3. Si `pong` reçu → l'émetteur envoie `dial {number, auto}` et tente
 *      `window.close()` puis affiche l'écran de succès.
 *   4. Si `busy` reçu → l'émetteur affiche l'écran d'erreur.
 *   5. Si rien reçu après 300 ms → flow normal (auth + navigate dashboard).
 *
 * Le canal est volontairement distinct de `callizy-takeover` pour ne pas
 * mélanger les sémantiques (gouvernance device vs. routing d'appel).
 */
const CHANNEL_NAME = 'callizy-clicktocall'

export type CallChannelMessage =
  | { type: 'ping'; correlationId: string }
  | { type: 'pong'; correlationId: string }
  | { type: 'busy'; correlationId: string }
  | { type: 'dial'; number: string; auto: boolean; correlationId: string }

export function getCallChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null
  return new BroadcastChannel(CHANNEL_NAME)
}

interface ListenerHandlers {
  /** true = appel en cours, false = libre, 'inactive' = onglet inactif, ne répond pas */
  isBusy: () => boolean | 'inactive'
  onDial: (number: string, auto: boolean) => void
}

/**
 * Hook côté dashboard : écoute le canal, répond pong/busy aux ping, et
 * appelle `onDial` quand un autre onglet demande de composer un numéro.
 *
 * Les handlers sont stables via useRef pour éviter de recréer le channel
 * à chaque render — sinon on raterait des messages pendant le swap.
 */
export function useClickToCallListener({ isBusy, onDial }: ListenerHandlers) {
  const handlersRef = useRef({ isBusy, onDial })
  handlersRef.current = { isBusy, onDial }

  useEffect(() => {
    const channel = getCallChannel()
    if (!channel) return

    const onMessage = (e: MessageEvent<CallChannelMessage>) => {
      const msg = e.data
      if (msg.type === 'ping') {
        const etat = handlersRef.current.isBusy()
        // Un onglet inactif se tait : l'émetteur n'entend personne, attend
        // 300 ms et ouvre le softphone lui-même. Répondre « busy » ici
        // affichait à tort « un appel est déjà en cours » à l'agent.
        if (etat === 'inactive') return
        channel.postMessage({
          type: etat ? 'busy' : 'pong',
          correlationId: msg.correlationId,
        } satisfies CallChannelMessage)
        return
      }
      if (msg.type === 'dial') {
        // Sanity check : on ne devrait pas recevoir un dial si on a répondu
        // busy. Mais on revérifie quand même au cas où un appel a démarré
        // entre le pong et le dial.
        if (handlersRef.current.isBusy()) return
        handlersRef.current.onDial(msg.number, msg.auto)
      }
    }

    channel.addEventListener('message', onMessage)
    return () => {
      channel.removeEventListener('message', onMessage)
      channel.close()
    }
  }, [])
}
